import { z } from 'zod';
import type { RefinementCtx } from 'zod';
import type { PartnershipAdSettingsFields } from '../types';

type PartnershipAdSettingsValidationContext = Pick<RefinementCtx, 'addIssue'>;

const adSettingIdSchema = z.union([
  z.number().int().positive(),
  z.string().trim().min(1, 'Can\'t be empty'),
]);

export const partnershipAdSettingsFieldSchema = z.object({
  page_id: adSettingIdSchema,
  text_id: adSettingIdSchema,
});

export const adSettingsFieldsSchema = z
  .record(partnershipAdSettingsFieldSchema)
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Ad settings are required',
  });

const isValidAdSettingId = (value: unknown): boolean => {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value > 0;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  return false;
};

export const isAdSettingsEntryEmpty = (
  settings: Record<string, unknown> | undefined,
): boolean => {
  if (!settings) {
    return true;
  }

  const keys = Object.keys(settings);
  if (!keys.length) {
    return true;
  }

  return keys.every((key) => {
    const value = settings[key];
    if (!value || typeof value !== 'object') {
      return true;
    }

    const entry = value as Record<string, unknown>;
    return !isValidAdSettingId(entry.page_id) && !isValidAdSettingId(entry.text_id);
  });
};

export const toPartnershipAdSettingsFields = (
  settings: Record<string, unknown> | undefined,
): PartnershipAdSettingsFields => adSettingsFieldsSchema.parse(settings ?? {});

export const validateAdSettingsFieldsAtIndex = (
  settings: Record<string, unknown> | undefined,
  index: number,
  ctx: PartnershipAdSettingsValidationContext,
): void => {
  const result = adSettingsFieldsSchema.safeParse(settings ?? {});

  if (result.success) {
    return;
  }

  result.error.issues.forEach((issue) => {
    ctx.addIssue({
      code: 'custom',
      message: issue.message,
      path: ['selectedAdSettings', index, ...issue.path],
    });
  });
};
